import React, { useState } from "react";
import {
    ChevronDown,
    ChevronRight,
    Plus,
    Trash2,
    GripVertical,
    ArrowUp,
    ArrowDown,
} from "lucide-react";
import ObjectFields, { makeBlank } from "./ObjectFields";

export default function ListEdit({ field, value, onChange, depth = 0 }) {
    const items = Array.isArray(value) ? value : [];
    const [openIdx, setOpenIdx] = useState(0);

    const updateItem = (i, next) => {
        const arr = items.slice();
        arr[i] = next;
        onChange(arr);
    };

    const move = (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= items.length) return;
        const arr = items.slice();
        const tmp = arr[i];
        arr[i] = arr[j];
        arr[j] = tmp;
        onChange(arr);
        setOpenIdx(j);
    };

    const removeAt = (i) => {
        if (!window.confirm("Remove this item?")) return;
        onChange(items.filter((_, idx) => idx !== i));
        setOpenIdx(-1);
    };

    const add = () => {
        onChange([...items, makeBlank(field.item_schema)]);
        setOpenIdx(items.length);
    };

    return (
        <div className="space-y-2">
            {items.map((it, i) => {
                const isOpen = openIdx === i;
                const label =
                    (field.item_label_field && it?.[field.item_label_field]) ||
                    `Item ${i + 1}`;
                return (
                    <div key={i} className="border border-slate-200 bg-white">
                        <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
                            <button
                                type="button"
                                onClick={() => setOpenIdx(isOpen ? -1 : i)}
                                className="flex items-center gap-2 text-[13.5px] text-slate-800 hover:text-teal-700"
                            >
                                {isOpen ? (
                                    <ChevronDown className="w-4 h-4" />
                                ) : (
                                    <ChevronRight className="w-4 h-4" />
                                )}
                                <GripVertical className="w-3.5 h-3.5 text-slate-400" />
                                <span className="font-medium truncate max-w-md">{label}</span>
                                {it && it.visible === false && (
                                    <span className="text-[10.5px] uppercase tracking-[0.15em] text-amber-700 bg-amber-50 px-1.5 py-0.5">
                                        Hidden
                                    </span>
                                )}
                            </button>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => move(i, -1)}
                                    disabled={i === 0}
                                    className="p-1 text-slate-500 hover:text-teal-700 disabled:opacity-30"
                                    title="Move up"
                                >
                                    <ArrowUp className="w-3.5 h-3.5" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => move(i, 1)}
                                    disabled={i === items.length - 1}
                                    className="p-1 text-slate-500 hover:text-teal-700 disabled:opacity-30"
                                    title="Move down"
                                >
                                    <ArrowDown className="w-3.5 h-3.5" />
                                </button>
                                {field.allow_add_remove !== false && (
                                    <button
                                        type="button"
                                        onClick={() => removeAt(i)}
                                        className="p-1 text-slate-500 hover:text-red-600"
                                        title="Remove"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>
                        </div>
                        {isOpen && (
                            <div className="p-4">
                                <ObjectFields
                                    schema={field.item_schema}
                                    value={it || {}}
                                    onChange={(next) => updateItem(i, next)}
                                    depth={depth + 1}
                                />
                            </div>
                        )}
                    </div>
                );
            })}
            {field.allow_add_remove !== false && (
                <button
                    type="button"
                    onClick={add}
                    className="inline-flex items-center gap-1 px-3 py-2 text-[13px] border border-slate-300 bg-white hover:bg-slate-50"
                >
                    <Plus className="w-4 h-4" /> Add {field.label || "item"}
                </button>
            )}
        </div>
    );
}
